import { useEffect, useMemo, useRef, useState } from 'react';
import { Download, Save, AlertTriangle, Upload, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import DesignCanvas from './DesignCanvas';
import PhotoFrames, { FrameToolbar, NO_CROP, startCrop, type Frame } from './PhotoFrames';
import GridGuides from './GridGuides';
import {
  BEFORE_AFTER_OFF, BEFORE_AFTER_ROLES, FONT_LABEL, IMAGE_ROLES, autoImage, autoText, canvasBlob, complianceReminders, complianceWarnings,
  MAX_ZOOM, fontProblems, isBeforeAfter, isBrand, kitImage, laidOut, uploadKitFile,
  type BrandKit, type Crop, type KitPerson, type Lang, type TemplateEntry,
} from '@/lib/brand-kit';
import { uploadAsset } from '@/lib/upload';
import { cn } from '@/lib/cn';

const ROLE_LABEL: Record<string, string> = {
  headline: 'Headline', subheadline: 'Subheadline', body: 'Body', caption: 'Caption', cta: 'Call to action', name: 'Name',
  title: 'Licensed title', specialties: 'Specialties', contact: 'Contact', signature: 'Signature', disclaimer: 'Disclaimer',
  photo: 'Photo', image: 'Image', logo: 'Logo', custom: 'Text',
  before_photo: 'Before photo', after_photo: 'After photo', label: 'Tag', icon: 'Icon',
};
const CONSENT = 'The patient signed written consent for these photos';
/** On a before and after the call to action is the benefit, never booking or contacts (owner 2026-10-23). */
const BENEFIT_LABEL = 'Benefit line (call to action)';
const BENEFIT_HINT = 'The benefit she gets, e.g. «بشرة أنعم بثقة أكبر». Empty = no bar, the photos re-centre';
const BOOKING = /whats ?app|واتساب|\bbook|\bcall\b|احجز|للحجز|اتصل|تواصل|\+?\d[\d\s-]{6,}|www\.|\.(ae|com)\b|@/i;

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40) || 'static';

/** Everyone on the clinic fills and exports. Brand assets come out as designed: only managers
 *  (canManage, clients.write) may change their words or photo here (Aziz 2026-10-22).
 *  The list is what this person may use (owner 2026-10-24): base, theirs, shared with them, and for
 *  owner and manager everyone's, grouped. */
export default function Generator({ clientId, kit, people, templates, canManage = true, canSave = true, forCreative, onSavedToPiece }: {
  clientId: string; kit: BrandKit; people: KitPerson[]; templates: TemplateEntry[]; canManage?: boolean;
  /** Making a piece's thumbnail from its posting card (Aziz 2026-10-30): a cover template is picked,
   *  the headline starts as the piece's title, and "Save to this piece" attaches the PNG to it */
  forCreative?: { id: string; code: string; title: string };
  onSavedToPiece?: () => void;
  /** "Save to clinic files" puts the PNG in the raw material library: a portal login needs material uploads */
  canSave?: boolean;
}) {
  const [tk, setTk] = useState(() => (forCreative
    ? templates.find((x) => /cover|thumbnail/i.test(`${x.template?.purpose ?? ''} ${x.template?.name ?? ''}`))?.key : undefined)
    ?? templates[0]?.key ?? '');
  const ti = Math.max(0, templates.findIndex((x) => x.key === tk));
  const [lang, setLang] = useState<Lang>('ar');
  const [pid, setPid] = useState('');
  const [sid, setSid] = useState('');
  const [texts, setTexts] = useState<Record<string, string>>({});
  const [images, setImages] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(0);   // photos still on their way to storage
  const [busy, setBusy] = useState<Record<string, number>>({});   // per frame, for its spinner
  // click a frame on the design to fill it, then move and zoom inside it (Aziz 2026-10-23)
  const [selFrame, setSelFrame] = useState<string | null>(null);
  const [sizes, setSizes] = useState<Record<string, { w: number; h: number }>>({});
  // before and after (Aziz 2026-10-22): the consent tick is per design and never stored as a fact
  const [consent, setConsent] = useState(false);
  const [crops, setCrops] = useState<Record<string, Crop>>({});
  // placement guides over the design while generating; never in the PNG (Aziz 2026-10-26)
  const [showGrid, setShowGrid] = useState(true);
  const [sameFraming, setSameFraming] = useState(true);
  // the pair follows you to another before and after design (another size): upload once, export every format
  const baPaths = useRef<Record<string, string>>({});
  const [fontNote, setFontNote] = useState<string[]>([]);
  const canvas = useRef<HTMLCanvasElement>(null);
  const replaceInput = useRef<HTMLInputElement>(null);
  const replaceFor = useRef('');
  const tBase = templates[ti]?.template;
  // the haze strength for this design (1 = the template's own); scales every photo fade, preview and PNG alike (Aziz 2026-10-26)
  const [haze, setHaze] = useState(1);
  const t = useMemo(() => !tBase || haze === 1 ? tBase : { ...tBase, elements: tBase.elements.map((e) => (e.scrim && e.scrim_strength
    ? { ...e, scrim_strength: Math.min(e.scrim_side === 'grid' ? 1.2 : 1, e.scrim_strength * haze) } : e)) }, [tBase, haze]);
  const hasHaze = !!tBase?.elements.some((e) => e.scrim && e.scrim_strength);
  const visible = people.filter((p) => p.info.shown !== false);
  const person = visible.find((p) => p.talent_id === pid);
  const service = kit.services.find((s) => s.id === sid);

  // refill from the kit whenever the template, language, person or service changes
  useEffect(() => {
    if (!t) return;
    const tx: Record<string, string> = {}; const im: Record<string, string> = {};
    for (const e of t.elements) {
      if (IMAGE_ROLES.includes(e.role)) im[e.id] = autoImage(kit, e, { lang, person, service }) || baPaths.current[e.role] || '';
      else if (e.role !== 'shape' && e.role !== 'icon') tx[e.id] = autoText(kit, e, { lang, person, service });
    }
    const head = forCreative && t.elements.find((e) => e.role === 'headline' && !e.hidden);
    if (head) tx[head.id] = forCreative!.title;
    setTexts(tx); setImages(im); setConsent(false); setSelFrame(null);
    // frames with a template placement start there (Aziz 2026-10-26), the rest start centred
    setCrops(Object.fromEntries(t.elements.filter((e) => e.default_crop).map((e) => [e.id, startCrop(e)])));
  }, [ti, lang, pid, sid, t?.id]);
  useEffect(() => { setHaze(1); }, [ti]);   // another template starts at its own haze // eslint-disable-line react-hooks/exhaustive-deps
  const ba = isBeforeAfter(t);
  // the clinic's own fonts or a visible warning, never a quiet fallback (owner 2026-10-23)
  useEffect(() => {
    if (!t) return;
    let live = true;
    fontProblems(clientId, kit, t, lang).then(({ missing, failed }) => {
      if (!live) return;
      setFontNote([
        ...(missing.length ? [`Upload the brand fonts in Brand kit > Fonts: no ${missing.map((s) => FONT_LABEL[s]).join(', ')} font yet, so this text would print in a stand-in font`] : []),
        ...(failed.length ? [`The brand font ${failed.map((s) => FONT_LABEL[s]).join(', ')} did not load: reload the page, or upload the file again in Brand kit > Fonts`] : []),
      ]);
    }).catch(() => undefined);
    return () => { live = false; };
  }, [clientId, t, lang, kit.type.fonts]); // eslint-disable-line react-hooks/exhaustive-deps
  const baAllowed = !!kit.compliance.before_after_allowed;
  const baPhotos = t ? t.elements.filter((e) => BEFORE_AFTER_ROLES.includes(e.role) && !e.hidden) : [];
  /** one photo's framing; with "Same framing" on, the two before and after photos move together */
  const setCrop = (id: string, next: Crop) => setCrops((m) => {
    const linked = sameFraming && baPhotos.some((q) => q.id === id);
    return linked ? { ...m, ...Object.fromEntries(baPhotos.map((q) => [q.id, next])) } : { ...m, [id]: next };
  });
  // the frames you can fill on the design: never the logo, never a brand asset without brand rights
  const frames: Frame[] = useMemo(() => !t ? [] : laidOut(t, lang, texts).elements
    .filter((e) => !e.hidden && IMAGE_ROLES.includes(e.role) && e.role !== 'logo'
      && !(BEFORE_AFTER_ROLES.includes(e.role) && !baAllowed) && !(!canManage && isBrand(kit, e)))
    .map((e) => ({ el: e, label: ROLE_LABEL[e.role], has: !!images[e.id], adjust: !!images[e.id] && !!sizes[e.id] && e.fit === 'cover', busy: !!busy[e.id] })),
  [t, lang, texts, images, sizes, busy, baAllowed, canManage, kit]); // eslint-disable-line react-hooks/exhaustive-deps
  const sel = frames.find((f) => f.el.id === selFrame && f.adjust);

  // Enforced (Aziz 2026-10-22): a static that breaks the clinic's rules cannot be exported or saved.
  const blocks = useMemo(() => {
    const b = complianceWarnings(kit, Object.values(texts), { template: t, texts });
    if (ba && !baAllowed) b.unshift(BEFORE_AFTER_OFF);
    if (ba && baAllowed) {
      for (const e of baPhotos) if (!images[e.id]) b.push(`Add the ${ROLE_LABEL[e.role].toLowerCase()}`);
      if (!consent) b.push(`Tick "${CONSENT}"`);
    }
    if (person && !person.info.ads_consent) b.push(`${person.name} has no signed consent to appear in ads`);
    if (service && !service.advertisable) b.push(`${service.name_en || service.name_ar} is marked not advertisable`);
    return b;
  }, [kit, texts, person, service, t, ba, baAllowed, images, consent]); // eslint-disable-line react-hooks/exhaustive-deps
  const warnings = useMemo(() => {
    const w: string[] = [];
    if (person && !person.info.photo && t?.elements.some((e) => e.role === 'photo'))
      w.push(`${person.name} has no photo in the brand kit yet: add one under Brand kit, People`);
    if (service?.sensitive) w.push(`${service.name_en || service.name_ar} is a sensitive service: owner approval needed before it runs`);
    w.push(...complianceReminders(kit, t));
    w.push(...fontNote);
    const bar = ba ? t?.elements.find((e) => e.role === 'cta' && !e.hidden) : undefined;
    if (bar && BOOKING.test(texts[bar.id] ?? '')) w.push('The benefit line is for the benefit she gets, not booking or contact details: those live in the ad itself');
    return w;
  }, [kit, texts, person, service, t, fontNote]);

  if (!t) return <Card><p className="text-sm text-muted">No templates yet. Build one on the Templates tab: upload a background and place the boxes.</p></Card>;

  const fileName = () => `${slug(kit.identity.brand_en || 'clinic')}-${slug(t.name)}-${person ? slug(person.name) + '-' : ''}${lang}.png`;
  const download = async () => {
    const b = await canvasBlob(canvas.current!);
    const a = document.createElement('a');
    a.href = URL.createObjectURL(b); a.download = fileName(); a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  };
  const save = async () => {
    setSaving(true);
    try {
      const b = await canvasBlob(canvas.current!);
      const file = new File([b], fileName(), { type: 'image/png' });
      // a before and after is tagged so the server can hold it to the clinic's switch and the consent tick
      const tags = ['brand-kit', t.purpose, ...(ba ? ['before-after', ...(consent ? ['patient-consent-confirmed'] : [])] : [])];
      await uploadAsset({ creativeId: '', material: { clientId, tags, doctor: person?.name }, file, kind: 'raw',
        notes: ba ? `Before and after. ${CONSENT}: ticked by the person who saved it.` : undefined });
      toast.success('Saved to the clinic files');
    } catch (e) { toast.error((e as Error).message || 'Could not save'); }
    finally { setSaving(false); }
  };
  const saveToPiece = async () => {
    if (!forCreative) return;
    setSaving(true);
    try {
      const b = await canvasBlob(canvas.current!);
      const file = new File([b], `${forCreative.code}-thumbnail.png`, { type: 'image/png' });
      await uploadAsset({ creativeId: forCreative.id, file, kind: 'thumbnail', notes: `Made from the brand kit (${t.name})` });
      toast.success(`Thumbnail saved to ${forCreative.code}`);
      onSavedToPiece?.();
    } catch (e) { toast.error((e as Error).message || 'Could not save'); }
    finally { setSaving(false); }
  };
  const pickImage = async (id: string, f?: File) => {
    if (!f) return;
    setUploading((n) => n + 1); setBusy((m) => ({ ...m, [id]: (m[id] ?? 0) + 1 }));
    // wait until the photo is back from storage too, so Download never exports the design without it
    try {
      const p = await uploadKitFile(clientId, 'image', f); await kitImage(clientId, p); setImages((m) => ({ ...m, [id]: p })); setConsent(false);
      const role = t.elements.find((e) => e.id === id)?.role;
      if (role && BEFORE_AFTER_ROLES.includes(role)) baPaths.current[role] = p;
      else {   // a new photo starts at the template's placement (default_crop), else centred
        const el = t.elements.find((e) => e.id === id);
        setCrops((m) => { const { [id]: _drop, ...rest } = m; return el?.default_crop ? { ...rest, [id]: startCrop(el) } : rest; });
      }
      setSelFrame(id);
    }
    catch (e) { toast.error((e as Error).message || 'Upload failed'); }
    finally { setUploading((n) => n - 1); setBusy((m) => ({ ...m, [id]: (m[id] ?? 1) - 1 })); }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_420px] gap-4">
      <Card>
        <div className="mx-auto" style={{ maxWidth: t.h > t.w ? 560 : 820 }}>
          <DesignCanvas clientId={clientId} kit={kit} template={t} lang={lang} texts={texts} images={images} crops={crops} canvasRef={canvas}
            onSizes={(s) => setSizes((o) => (JSON.stringify(o) === JSON.stringify(s) ? o : s))}
            overlay={<>{showGrid && <GridGuides w={t.w} h={t.h} />}<PhotoFrames w={t.w} h={t.h} frames={frames} crops={crops} sizes={sizes} selected={selFrame} onSelect={setSelFrame}
              onPick={(id, f) => pickImage(id, f)} onCrop={setCrop} /></>} />
          <label className="mt-2 inline-flex items-center gap-2 text-xs text-muted-strong cursor-pointer">
            <input type="checkbox" checked={showGrid} onChange={(e) => setShowGrid(e.target.checked)} data-testid="gen-show-grid" />
            Show the grid and reel guides (never in the download)
          </label>
          {hasHaze && (
            <label className="mt-2 flex items-center gap-3 text-xs text-muted-strong">
              <span className="w-12">Haze</span>
              <input type="range" min={0} max={1.2} step={0.05} value={haze} onChange={(e) => setHaze(Number(e.target.value))} className="flex-1" aria-label="Haze strength" data-testid="gen-haze" />
              <span className="w-10 tabular-nums" data-testid="gen-haze-value">{Math.round(haze * 100)}%</span>
              {haze !== 1 && <button type="button" className="text-accent-400 hover:underline" onClick={() => setHaze(1)} data-testid="gen-haze-reset">Reset</button>}
            </label>
          )}
          <input ref={replaceInput} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="frame-replace-file"
            onChange={(ev) => { pickImage(replaceFor.current, ev.target.files?.[0]); ev.target.value = ''; }} />
          {sel ? (
            <FrameToolbar frame={sel} crop={crops[sel.el.id] ?? startCrop(sel.el)} size={sizes[sel.el.id]}
              linked={sameFraming && baPhotos.length > 1 && BEFORE_AFTER_ROLES.includes(sel.el.role)}
              onCrop={(c) => setCrop(sel.el.id, c)} onDone={() => setSelFrame(null)}
              onReplace={() => { replaceFor.current = sel.el.id; replaceInput.current?.click(); }} />
          ) : frames.length > 0 && (
            <p className="mt-3 text-xs text-muted" data-testid="frame-hint">Click a photo frame on the design to upload into it. Click a filled frame to move and zoom the photo.</p>
          )}
        </div>
      </Card>
      <div className="flex flex-col gap-4">
        <Card>
          <div className="grid gap-3">
            <Select label="Template" value={templates[ti]?.key ?? ''} onChange={setTk} data-testid="gen-template" searchable={templates.length > 8}
              options={templates.flatMap((x, i) => [
                ...(i === 0 || templates[i - 1].group !== x.group ? [{ value: `group:${x.group}`, label: x.group, disabled: true }] : []),
                { value: x.key, label: x.template.name, hint: `${x.template.w}x${x.template.h}` }])} />
            <div className="flex gap-1">
              {(['ar', 'en'] as const).filter((l) => t.lang === 'both' || t.lang === l).map((l) => (
                <button key={l} onClick={() => setLang(l)} data-testid={`gen-lang-${l}`}
                  className={cn('px-3 h-8 rounded text-xs', lang === l ? 'bg-accent-500/20 text-white' : 'text-muted')}>{l === 'en' ? 'English' : 'العربية'}</button>
              ))}
            </div>
            <Select label="Doctor" value={pid} onChange={setPid} data-testid="gen-person"
              options={[{ value: '', label: 'Nobody' }, ...visible.map((p) => ({ value: p.talent_id, label: p.name, hint: p.info.ads_consent ? '' : 'no consent' }))]} />
            <Select label="Service" value={sid} onChange={setSid} data-testid="gen-service"
              options={[{ value: '', label: 'None' }, ...kit.services.map((s) => ({ value: s.id, label: s.name_en || s.name_ar, hint: s.sensitive ? 'sensitive' : '' }))]} />
          </div>
        </Card>
        <Card>
          <h3 className="font-medium text-muted-strong mb-3">Content</h3>
          <div className="grid gap-3">
            {t.elements.filter((e) => e.role !== 'shape' && e.role !== 'icon').map((e) => IMAGE_ROLES.includes(e.role) ? (
              e.role === 'logo' ? null : BEFORE_AFTER_ROLES.includes(e.role) && !baAllowed ? (
                <span key={e.id} className="inline-flex items-center gap-2 text-sm text-muted" data-testid={`gen-ba-off-${e.role}`}>
                  <Lock size={14} /> {ROLE_LABEL[e.role]}: before and after is switched off
                </span>
              ) : !canManage && isBrand(kit, e) ? (
                <span key={e.id} className="inline-flex items-center gap-2 text-sm text-muted" title="Brand asset: only managers and people with Brand details change this" data-testid={`gen-brand-${e.role}`}>
                  <Lock size={14} /> {ROLE_LABEL[e.role]}: brand asset
                </span>
              ) : (
                <label key={e.id} className="inline-flex items-center gap-2 text-sm text-accent-400 cursor-pointer hover:underline" data-testid={`gen-upload-${e.role}`}>
                  <Upload size={14} /> {BEFORE_AFTER_ROLES.includes(e.role) ? `${ROLE_LABEL[e.role]}: ${images[e.id] ? 'replace' : 'upload'}` : `${ROLE_LABEL[e.role]}: ${images[e.id] ? 'replace' : 'add'} an image`}
                  <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(ev) => { pickImage(e.id, ev.target.files?.[0]); ev.target.value = ''; }} />
                </label>
              )
            ) : (
              <label key={e.id} className="flex flex-col gap-1 text-sm">
                <span className="text-xs text-muted" title={!canManage && isBrand(kit, e) ? 'Brand asset: only managers and people with Brand details change this' : undefined}>
                  {ba && e.role === 'cta' ? BENEFIT_LABEL : ROLE_LABEL[e.role]}{e.role === 'label' && e.default_en ? `: ${e.default_en}` : ''}{!canManage && isBrand(kit, e) ? ' (brand asset)' : ''}
                </span>
                <textarea rows={e.max_lines > 2 ? 3 : 1} dir={lang === 'ar' ? 'rtl' : 'ltr'} value={texts[e.id] ?? ''} disabled={!canManage && isBrand(kit, e)}
                  placeholder={ba && e.role === 'cta' ? BENEFIT_HINT : undefined}
                  onChange={(ev) => setTexts((m) => ({ ...m, [e.id]: ev.target.value }))} data-testid={`gen-text-${e.role}`}
                  className="rounded-lg border border-slate-800 bg-bg px-3 py-2 text-sm text-muted-strong disabled:opacity-60" />
              </label>
            ))}
          </div>
        </Card>
        {ba && baAllowed && (
          <Card data-testid="gen-ba">
            <h3 className="font-medium text-muted-strong mb-2">Before and after</h3>
            <p className="text-xs text-muted mb-3">Both photos fill identical cards and are never stretched. Zoom and move them so the two faces line up.</p>
            <label className="flex items-center gap-2 text-sm text-muted-strong mb-3">
              <input type="checkbox" checked={sameFraming} onChange={(e) => setSameFraming(e.target.checked)} data-testid="gen-ba-same" /> Same framing for both photos
            </label>
            {(sameFraming ? baPhotos.slice(0, 1) : baPhotos).map((p) => {
              const c = crops[p.id] ?? NO_CROP;
              const setC = (patch: Partial<Crop>) => setCrop(p.id, { ...c, ...patch });
              const slider = (label: string, k: keyof Crop, min: number, max: number) => (
                <label key={k} className="grid grid-cols-[70px_1fr] items-center gap-2 text-xs text-muted">{label}
                  <input type="range" min={min} max={max} step={0.01} value={c[k]} onChange={(e) => setC({ [k]: Number(e.target.value) })} data-testid={`gen-crop-${k}`} />
                </label>
              );
              return (
                <div key={p.id} className="grid gap-1 mb-2">
                  <span className="text-xs text-muted-strong">{sameFraming ? 'Both photos' : ROLE_LABEL[p.role]}</span>
                  {slider('Zoom', 'zoom', 1, MAX_ZOOM)}{slider('Left, right', 'x', -1, 1)}{slider('Up, down', 'y', -1, 1)}
                </div>
              );
            })}
            <label className="flex items-start gap-2 text-sm text-muted-strong mt-2">
              <input type="checkbox" className="mt-1" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="gen-ba-consent" /> {CONSENT}
            </label>
          </Card>
        )}
        {blocks.length > 0 && (
          <Card className="border-red-500/50" data-testid="gen-blocks">
            <p className="text-sm font-medium text-red-300 mb-1">Fix these before it can be exported</p>
            {blocks.map((w) => <p key={w} className="text-sm text-red-300 flex gap-2"><AlertTriangle size={14} className="mt-0.5 shrink-0" />{w}</p>)}
          </Card>
        )}
        {warnings.length > 0 && (
          <Card className="border-amber-500/40" data-testid="gen-warnings">
            {warnings.map((w) => <p key={w} className="text-sm text-amber-300 flex gap-2"><AlertTriangle size={14} className="mt-0.5 shrink-0" />{w}</p>)}
          </Card>
        )}
        {uploading > 0 && <p className="text-sm text-accent-400" data-testid="gen-uploading">Uploading photo... the design updates when it lands.</p>}
        <div className="flex gap-2">
          <Button icon={<Download size={14} />} onClick={download} disabled={blocks.length > 0 || uploading > 0} data-testid="gen-download">Download PNG</Button>
          {forCreative && <Button icon={<Save size={14} />} loading={saving} onClick={saveToPiece} disabled={blocks.length > 0 || uploading > 0} data-testid="gen-save-piece">Save to this piece</Button>}
          {canSave && <Button variant="secondary" icon={<Save size={14} />} loading={saving} onClick={save} disabled={blocks.length > 0 || uploading > 0} data-testid="gen-save">Save to clinic files</Button>}
        </div>
      </div>
    </div>
  );
}
